/**
 * Transactional email helpers — calls the internal /api/email/send route
 * which proxies to the Supabase Edge Function (send-transactional-email).
 */

const TIER_LABELS: Record<string, string> = {
  student: 'Student Plan',
  undergraduate: 'Undergraduate Plan',
  premium: 'Premium Plan',
  free: 'Free Plan',
};

const TIER_AMOUNTS: Record<string, number> = {
  student: 500,
  undergraduate: 1000,
  premium: 2000,
};

async function sendEmail(type: string, to: string, data: Record<string, string | number>) {
  try {
    const res = await fetch('/api/email/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type, to, data }),
    });
    if (!res.ok) {
      const err = await res.json();
      console.error(`[Email] Failed to send ${type}:`, err);
    }
  } catch (err) {
    console.error(`[Email] Network error sending ${type}:`, err);
  }
}

/**
 * Send subscription confirmation email after successful M-Pesa payment.
 */
export async function sendSubscriptionConfirmation({
  email,
  tier,
  receipt,
  amount,
}: {
  email: string;
  tier: string;
  receipt: string;
  amount: number;
}) {
  const next = new Date();
  next.setMonth(next.getMonth() + 1);
  const nextBillingDate = next.toLocaleDateString('en-KE', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  await sendEmail('subscription_confirmation', email, {
    tier_label: TIER_LABELS[tier] || tier,
    amount,
    receipt,
    next_billing_date: nextBillingDate,
  });
}

/**
 * Send billing reminder email before next billing date.
 */
export async function sendBillingReminder({
  email,
  tier,
  billingDate,
  daysUntilBilling,
}: {
  email: string;
  tier: string;
  billingDate: string;
  daysUntilBilling: number;
}) {
  await sendEmail('billing_reminder', email, {
    tier_label: TIER_LABELS[tier] || tier,
    amount: TIER_AMOUNTS[tier] || 0,
    billing_date: billingDate,
    days_until_billing: daysUntilBilling,
  });
}

/**
 * Send referral commission earned notification to ambassador.
 */
export async function sendReferralCommissionNotification({
  email,
  commissionAmount,
  referredPlan,
  commissionRate,
  lifetimeEarnings,
}: {
  email: string;
  commissionAmount: number;
  referredPlan: string;
  commissionRate: number;
  lifetimeEarnings: number;
}) {
  await sendEmail('referral_commission', email, {
    commission_amount: commissionAmount,
    referred_plan: TIER_LABELS[referredPlan] || referredPlan,
    commission_rate: Math.round(commissionRate * 100),
    lifetime_earnings: lifetimeEarnings,
  });
}

/**
 * Send referral signup notification to ambassador when a new user signs up via their code.
 */
export async function sendReferralSignupNotification({
  email,
  newUserName,
  referralCode,
  totalReferrals,
}: {
  email: string;
  newUserName: string;
  referralCode: string;
  totalReferrals: number;
}) {
  await sendEmail('referral_signup', email, {
    new_user_name: newUserName,
    referral_code: referralCode,
    total_referrals: totalReferrals,
  });
}

/**
 * Notify senior that their scholar has composed an answer (pending admin review).
 */
export async function sendScholarAnsweredNotification({
  email,
  scholarName,
  questionPreview,
}: {
  email: string;
  scholarName: string;
  questionPreview: string;
}) {
  await sendEmail('scholar_answered', email, {
    scholar_name: scholarName,
    question_preview: questionPreview.length > 100 ? questionPreview.slice(0, 97) + '…' : questionPreview,
  });
}

/**
 * Notify senior that their answer has been approved and delivered to their inbox.
 */
export async function sendAnswerApprovedNotification({
  email,
  scholarName,
  questionPreview,
}: {
  email: string;
  scholarName: string;
  questionPreview: string;
}) {
  await sendEmail('answer_approved', email, {
    scholar_name: scholarName,
    question_preview: questionPreview.length > 100 ? questionPreview.slice(0, 97) + '…' : questionPreview,
  });
}

/**
 * Notify a subscribed senior that a scholar they follow has published a new approved answer.
 */
export async function sendScholarPublishedNotification({
  email,
  scholarName,
  questionPreview,
}: {
  email: string;
  scholarName: string;
  questionPreview: string;
}) {
  await sendEmail('scholar_published', email, {
    scholar_name: scholarName,
    question_preview: questionPreview.length > 100 ? questionPreview.slice(0, 97) + '…' : questionPreview,
  });
}

/**
 * Notify a Sheikh that a new question has been assigned to them.
 */
export async function sendQuestionAssignedNotification({
  email,
  sheikhName,
  seniorName,
  seniorInitials,
  questionPreview,
  specialisation,
}: {
  email: string;
  sheikhName: string;
  seniorName: string;
  seniorInitials: string;
  questionPreview: string;
  specialisation: string;
}) {
  await sendEmail('question_assigned', email, {
    sheikh_name: sheikhName,
    senior_name: seniorName,
    senior_initials: seniorInitials,
    question_preview: questionPreview.length > 200 ? questionPreview.slice(0, 197) + '…' : questionPreview,
    specialisation,
  });
}

/**
 * Send scholar email verification link.
 */
export async function sendScholarEmailVerification({
  email,
  scholarName,
  verificationUrl,
}: {
  email: string;
  scholarName: string;
  verificationUrl: string;
}) {
  await sendEmail('scholar_email_verification', email, {
    scholar_name: scholarName,
    verification_url: verificationUrl,
  });
}
